import { useEffect, useRef } from 'react';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Heading2, Eraser } from 'lucide-react';

/**
 * Editor de texto com formatação simples (negrito, itálico, listas, links, títulos).
 * Usa contentEditable + execCommand — leve, sem dependências. Emite HTML via onChange.
 */
export default function RichText({
  value,
  onChange,
  minHeight = 160,
}: {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Sincroniza o valor externo só quando o editor NÃO está em foco (evita pular o cursor).
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && (value || '') !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  function emit() {
    onChange(ref.current?.innerHTML || '');
  }
  function exec(cmd: string, arg?: string) {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  }
  function addLink() {
    const url = window.prompt('Endereço do link (https://...)');
    if (url) exec('createLink', url);
  }

  const Btn = ({ onClick, title, children }: any) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-secondary"
    >
      {children}
    </button>
  );

  return (
    <div className="mt-1 border border-gray-300 dark:border-dark-border rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary px-2 py-1">
        <Btn onClick={() => exec('bold')} title="Negrito"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('italic')} title="Itálico"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('formatBlock', 'H2')} title="Título"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('insertUnorderedList')} title="Lista"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('insertOrderedList')} title="Lista numerada"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn onClick={addLink} title="Inserir link"><LinkIcon className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('removeFormat')} title="Limpar formatação"><Eraser className="w-4 h-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onBlur={emit}
        style={{ minHeight }}
        className="px-3 py-2 text-sm leading-relaxed outline-none dark:text-dark-text-primary [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-primary-600 [&_a]:underline"
        suppressContentEditableWarning
      />
    </div>
  );
}
